import { Button } from "@egaranti/components";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@egaranti/components";
import { Popover, PopoverContent, PopoverTrigger } from "@egaranti/components";

import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import productService from "@/services/productService";
import sellerService from "@/services/sellerService";

import { Check } from "lucide-react";

import useDebounce from "@/hooks/useDebounce";
import { cn } from "@/lib/utils";

export function SelectDocument({ value, setValue, type }) {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");

  // when first opened, fetch documents
  useEffect(() => {
    if (open) {
      setSearch("");
      setLoading(true);
      productService
        .getDocumentList({
          type,
          page: 1,
          size: 5,
        })
        .then((res) => {
          setData(
            res.content.map((document) => ({
              label: document.name,
              value: document.id,
            })),
          );
          setLoading(false);
        });
    }
  }, [open]);

  const onChangeSearch = (event) => {
    setSearch(event);

    handleSearch(event);
  };

  const handleSearch = useDebounce((e) => {
    setLoading(true);
    productService
      .getDocumentList({
        type,
        page: 1,
        size: 5,
        name: e,
      })
      .then((res) => {
        setData(
          res.content.map((document) => ({
            label: document.name,
            value: document.id,
          })),
        );

        setLoading(false);
      })
      .catch(() => {
        // Handle any errors here
        console.error("Error while fetching documents");
      });
  }, 750);

  let buttonLabel = t("productsPage.searchDocuments.placeholder");

  if (value) {
    const selectedDocument = data.find(
      (document) => document.value.toString() === value.toString(),
    );
    if (selectedDocument) {
      buttonLabel = selectedDocument.label;
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="secondaryGray"
          role="combobox"
          aria-expanded={open}
          className="ring-offset-background focus:ring-ring flex w-full items-center justify-between rounded-lg border border-[#CDD5E0] px-4 py-6 text-[#111729] placeholder:text-[#677389] focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 [&>span]:line-clamp-1"
        >
          {buttonLabel}
          <svg
            className="h-5 w-5"
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            fill="none"
            viewBox="0 0 20 20"
          >
            <path
              stroke="#677389"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.667"
              d="M5 7.5l5 5 5-5"
            ></path>
          </svg>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full min-w-[350px] p-0">
        <Command shouldFilter={false} className="overflow-visible">
          <CommandInput
            value={search}
            onValueChange={(e) => onChangeSearch(e)}
            className="w-full"
            placeholder={t("productsPage.searchDocuments.placeholder")}
          />
          <CommandEmpty>{t("common.noData")}</CommandEmpty>
          <CommandGroup className="h-full overflow-auto">
            {data?.map((document) => (
              <CommandItem
                onSelect={() => {
                  setValue(document.value.toString());
                  setOpen(false);
                }}
                key={document.value}
                value={document.value}
              >
                <Check
                  className={cn(
                    "mr-2 h-4 w-4",
                    (value === document.value && "opacity-100") || "opacity-0",
                  )}
                />
                {document.label.length > 24
                  ? document.label.slice(0, 24) + "..."
                  : document.label}
              </CommandItem>
            ))}
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default SelectDocument;
